import styled from 'styled-components';

import { createColumnHelper } from '@tanstack/react-table';

import Tooltip from 'components/shared/Tooltip';
import { simpleDate } from 'lib/date';
import { DenialListItem, SEX_DISPLAY } from 'models/DenialListItem';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ThreeDotMenu from 'svg/ThreeDotMenu';

const columnHelper = createColumnHelper<DenialListItem>();

const EmptyValue = () => <>&mdash;</>;

export default function denialListColumns() {
  return [
    columnHelper.accessor('patientName', {
      id: 'patientName',
      header: 'Patient Name',
      cell: (info) => <BodySmallBold>{info.getValue() || <EmptyValue />}</BodySmallBold>,
      size: 160,
    }),
    columnHelper.accessor('dateOfBirth', {
      id: 'dateOfBirth',
      header: 'DOB',
      cell: (info) => <BodySmall>{simpleDate(info.getValue()) ?? <EmptyValue />}</BodySmall>,
      size: 110,
    }),
    columnHelper.accessor('sex', {
      id: 'sex',
      header: 'Gender',
      cell: (info) => <BodySmall>{SEX_DISPLAY[info.getValue()] ?? <EmptyValue />}</BodySmall>,
      size: 110,
    }),
    columnHelper.accessor('reason', {
      id: 'reason',
      header: 'Reason',
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <EmptyValue />;

        return (
          <Tooltip text={value} position='bottom'>
            <TruncatedReason>{value}</TruncatedReason>
          </Tooltip>
        );
      },
      size: 320,
    }),
    columnHelper.accessor('dateAdded', {
      id: 'dateAdded',
      header: 'Date Added',
      cell: (info) => <BodySmall>{simpleDate(info.getValue()) ?? <EmptyValue />}</BodySmall>,
      size: 120,
      // Default-desc column: descending is the first sort direction so clicking the
      // header toggles desc -> asc instead of dropping straight to an unsorted state.
      sortDescFirst: true,
    }),
    columnHelper.display({
      id: 'menu',
      // Placeholder for the per-row actions menu wired up in OLIO-2459 (create/edit/delete).
      cell: () => (
        <KebabButton type='button' disabled aria-label='Row actions'>
          <ThreeDotMenu />
        </KebabButton>
      ),
      size: 40,
      meta: { style: { justifyContent: 'flex-end' } },
    }),
  ];
}

const TruncatedReason = styled(BodySmall)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${colors.black75};
`;

const KebabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: none;
  background: transparent;
  cursor: not-allowed;
  opacity: 0.5;
`;
